import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { Radio } from '../../../../../../widgets/radio';
import { AdvancedSelect } from '../../../../../../widgets/select';
import React from 'react';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import ComposerUploadComponent from '../components/composerUploadComponent';
import Stack from '../../../../../../widgets/stack/stack';
import { ValidationBox } from '../../../../../../widgets/validation/validationBox';

export enum ComposerScreenshotType {
    Object,
    Material,
    Upload
}
const typeSelector = <T,>(type: ComposerScreenshotType, choice1: T, choice2: T, choice3: T): T => {
    return type === ComposerScreenshotType.Object ? choice1 : type === ComposerScreenshotType.Material ? choice2 : choice3;
};
interface ComposerScreenshotOutput {
    type: ComposerScreenshotType;
    attributeFilter: string;
    optionsFilter: string;
    material?: string;
    upload?: string;
}
interface ComposerScreenshotAttributeDialogProps {
    materialsList: BABYLON.Material[];
    onConfirm: (attributes: ComposerScreenshotOutput) => void;
}
const ComposerSaveAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
`;
const Title = styled.h3`
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

const ComposerScreenshotAttributeDialog: React.FC<ComposerScreenshotAttributeDialogProps> = ({ materialsList, onConfirm }) => {
    const validationContext = useValidationContext();
    const [type, setType] = React.useState<ComposerScreenshotType>(ComposerScreenshotType.Object);
    const [attributeFilter, setAttributeFilter] = React.useState<string>('');
    const [optionsFilter, setOptionsFilter] = React.useState<string>('');

    const [material, setMaterial] = React.useState<string | undefined>(undefined);
    const [upload, setUpload] = React.useState<string | undefined>(undefined);

    const typeOptions = React.useMemo(
        () => [
            { value: ComposerScreenshotType.Object, label: T._('Object', 'Admin') },
            { value: ComposerScreenshotType.Material, label: T._('Material', 'Admin') },
            { value: ComposerScreenshotType.Upload, label: T._('Upload', 'Admin') }
        ],
        []
    );

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const screenshot: ComposerScreenshotOutput = {
            type,
            attributeFilter,
            optionsFilter,
            material,
            upload
        };
        onConfirm(screenshot);
    };

    return (
        <Dialog windowDecorator={ComposerSaveAttributeDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Title>{T._('Apply Screenshot to Multiple Options', 'Admin')}</Title>
            <Container>
                <FormControl label={T._('Screenshot type', 'Admin')} tooltipLabelHtml={T._('Select the type of screenshot to apply', 'Admin')}>
                    {typeOptions.map((option) => (
                        <Radio
                            key={option.value}
                            name='screenshotType'
                            label={option.label}
                            value={option.value}
                            checked={type === option.value}
                            onSelect={() => setType(option.value)}
                        />
                    ))}
                </FormControl>
                {typeSelector<React.ReactNode>(
                    type,
                    <></>,
                    <FormControl label={T._('Material', 'Admin')} tooltipLabelHtml={T._('Screenshot will be fetched from the material', 'Admin')}>
                        <AdvancedSelect
                            value={material}
                            onChange={(e) => setMaterial(e)}
                            validationKey='composerScreenshotMaterial'
                            validationLabel={T._('Material', 'Admin')}
                            validate={(value) => type === ComposerScreenshotType.Material && validateRequiredTrimmed(value?.toString())}
                            validationMessage={T._('Material is required.', 'Admin')}
                        >
                            <option value=''>{T._('Select a material', 'Admin')}</option>
                            {materialsList.map(({ id, name }) => (
                                <option key={id} value={id}>
                                    {name}
                                </option>
                            ))}
                        </AdvancedSelect>
                    </FormControl>,
                    <FormControl label={T._('Upload', 'Admin')} tooltipLabelHtml={T._('Screenshot will be uploaded from your computer', 'Admin')}>
                        <ValidationBox
                            fullWidth
                            validationKey='composerScreenshotUpload'
                            validationLabel={T._('Upload', 'Admin')}
                            validationValue={upload}
                            validate={(value) => type === ComposerScreenshotType.Upload && validateRequiredTrimmed(value)}
                            validationMessage={T._('Upload is required.', 'Admin')}
                        >
                            <ComposerUploadComponent
                                onImagePick={(url) => {
                                    setUpload(url);
                                }}
                            />
                        </ValidationBox>
                    </FormControl>
                )}
                <Stack>
                    <FormControl
                        label={T._('Attributes filter (optional)', 'Admin')}
                        tooltipLabelHtml={T._('The attributes filter excludes only attributes that match the filter', 'Admin')}
                    >
                        <Input
                            placeholder={T._('You can use wildcards like *', 'Admin')}
                            type='text'
                            value={attributeFilter}
                            onChange={(e) => setAttributeFilter(e.target.value)}
                        />
                    </FormControl>
                    <FormControl
                        label={T._('Options filter (optional)', 'Admin')}
                        tooltipLabelHtml={T._('The name of the attribute that will be displayed in the configurator', 'Admin')}
                    >
                        <Input
                            placeholder={T._('You can use wildcards like *', 'Admin')}
                            type='text'
                            value={optionsFilter}
                            onChange={(e) => setOptionsFilter(e.target.value)}
                        />
                    </FormControl>
                </Stack>
            </Container>
        </Dialog>
    );
};

const ComposerScreenshotAttributeDialogWithValidation: React.FC<ComposerScreenshotAttributeDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerScreenshotAttributeDialog {...props} />
    </ValidationStateProvider>
);

export default ComposerScreenshotAttributeDialogWithValidation;
